import React, { Fragment, PureComponent } from 'react';
import { ActualPack, VirtualPack } from '~/Api/Analytics/VirtualBrand';
import { Country } from '~/Api/Country';
import { ProductPrice } from '~/Api/Product';
import { LicenseProvider } from '~/FormattedProductPrice';
import Button from '~/UI/Button';
import CountryIcon from '~/UI/CountryIcon';
import Icon from '~/UI/Icon/Icon';
import Tooltip from '~/UI/Tooltip';

import arePacksEqual from './arePacksEqual';
import styles from './styles.less';
import UnitPrice from '~/Api/Product/UnitPrice';
import TreatmentCost from '~/Api/Product/TreatmentCost';

interface Props {
	brandName: string;
	virtualPack: VirtualPack;
	countries: Country[];
	styles: {
		container: string;
		header: string;
		headerLeftColumn: string;
		headerRightColumn: string;
		heading: string;
		table: string;
		countryHeadCell: string;
		companyHeadCell: string;
		priceHeadCell: string;
		cell: string;
		companyCell: string;
		priceCell: string;
		countryIcon: string;
		button: string;
	};
	onCreateList(): void;
}

export default class PackPricesTable extends PureComponent<Props> {
	static defaultProps = {
		styles,
	};

	resolveActualPacks(): ActualPack[] {
		const { virtualPack, countries } = this.props;
		const countryCodes = new Set(countries.map((country) => country.alpha2));
		const filteredActualPacks = virtualPack.validActualPacks.filter((actualPack) => {
			return (
				(countryCodes.size === 0 || countryCodes.has(actualPack.dataSource.country.alpha2)) && actualPack.hasPrices
			);
		});

		filteredActualPacks.sort((pack, otherPack) => {
			return (
				pack.dataSource.country.alpha2.localeCompare(otherPack.dataSource.country.alpha2) ||
				pack.dataSource.shortName.localeCompare(otherPack.dataSource.shortName) ||
				(pack.companyName || '').localeCompare(otherPack.companyName || '')
			);
		});

		return filteredActualPacks.filter((actualPack, i) => {
			return i === 0 || !arePacksEqual(actualPack, filteredActualPacks[i - 1]);
		});
	}

	renderPrices(actualPack: ActualPack) {
		const { dataSource } = actualPack;

		return (
			<LicenseProvider dataSource={dataSource}>
				{(isLicenced) => {
					if (isLicenced === null) {
						return null;
					}
					if (dataSource.requiresLicence.prices && !isLicenced) {
						return (
							<td className={styles.textCell} colSpan={7}>
								prices restricted in trial
							</td>
						);
					}

					const { pricesMap } = actualPack;

					return (
						<Fragment>
							{this.renderPrice(pricesMap['ex-factory'])}
							{this.renderPrice(pricesMap['ex-factory-with-vat'])}
							{this.renderPrice(pricesMap.wholesale)}
							{this.renderPrice(pricesMap['wholesale-with-vat'])}
							{this.renderPrice(pricesMap.retail)}
							{this.renderPrice(pricesMap['retail-with-vat'])}
							{this.renderUnitPrice(actualPack.unitPrice)}
							{this.renderTreatmentCost(actualPack.treatmentCost)}
						</Fragment>
					);
				}}
			</LicenseProvider>
		);
	}

	renderUnitPrice(unitPrice: UnitPrice | null) {
		return <td className={styles.priceCell}>{unitPrice?.roundedHumanFormat()}</td>;
	}

	renderTreatmentCost(treatmentCost: TreatmentCost | null) {
		return <td className={styles.priceCell}>{treatmentCost?.roundedHumanFormat()}</td>;
	}

	renderPrice(packPrice: ProductPrice | null) {
		return (
			<td className={styles.priceCell}>
				{packPrice?.origin?.isComputed ? this.renderCalculatedPriceTooltip() : null}
				{packPrice?.price?.roundedHumanFormat()}
			</td>
		);
	}

	renderCalculatedPriceTooltip() {
		return (
			<Tooltip
				reference={
					<span>
						<Icon name='exfactory-question-mark' />{' '}
					</span>
				}
			>
				Provided price is calculated.
			</Tooltip>
		);
	}

	render() {
		const { brandName, virtualPack, styles, onCreateList } = this.props;

		return (
			<div className={styles.container}>
				<div className={styles.header}>
					<div className={styles.headerLeftColumn}>
						<h3 className={styles.heading}>
							{brandName} {virtualPack.distinctName}
						</h3>
					</div>
					<div className={styles.headerRightColumn}>
						<Button styles={styles} onClick={onCreateList}>
							Create list
						</Button>
					</div>
				</div>
				<table className={styles.table}>
					<thead>
						<tr>
							<th className={styles.countryHeadCell}>Country</th>
							<th className={styles.companyHeadCell}>Company</th>
							<th className={styles.priceHeadCell}>Ex-F</th>
							<th className={styles.priceHeadCell}>Ex-F + VAT</th>
							<th className={styles.priceHeadCell}>WH</th>
							<th className={styles.priceHeadCell}>WH + VAT</th>
							<th className={styles.priceHeadCell}>Retail</th>
							<th className={styles.priceHeadCell}>Retail + VAT</th>
							<th className={styles.priceHeadCell}>Unit Price</th>
							<th className={styles.priceHeadCell}>Treatment Cost</th>
						</tr>
					</thead>
					<tbody>
						{this.resolveActualPacks().map((actualPack) => {
							return (
								<tr key={actualPack.id}>
									<td className={styles.cell}>
										<span className={styles.countryIcon}>
											<CountryIcon
												code={actualPack.dataSource.country.alpha2}
												name={actualPack.dataSource.country.name}
											/>
										</span>{' '}
										{actualPack.dataSource.country.alpha2} - {actualPack.dataSource.shortName}
									</td>
									<td className={styles.companyCell}>{actualPack.companyName}</td>
									{this.renderPrices(actualPack)}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		);
	}
}
